import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Card from "../common/components/Card";
import Checkbox from "../common/components/Checkbox";
import DefaultButton from "../common/components/DefaultButton";
import { Col, Row } from "../common/components/Grid";
import ReactForm from "../common/components/ReactForm";
import useClientConfig from "../common/hooks/clientConfigHook";
import useNetwork from "../common/hooks/networkHook";
import { createAccountRequest } from "../reducers/accountRequestSlice";
import AccessDropdown from "./AccessDropdown";
import AccountRequestDetails from "./AccountRequestDetails";
import LevelDropdown from "./LevelDropdown";
import { usePublicNavBar } from "./NavBar";
import SiteDropdown from "./SiteDropdown";
import TypeDropdown from "./TypeDropdown";

const RequestAccountImpl = () => {
    const { t } = useTranslation();
    const network = useNetwork();
    const clientConfig = useClientConfig();
    const [request, setRequest] = useState({ customfields: { }, role: [{ access: 1 }] });
    const [sitrep, setSitrep] = useState(false);
    const [log, setLog] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [success, setSuccess] = useState();
    const formRef = useRef();

    useEffect(() => {
        if (clientConfig?.accountRequest?.site) {
            setRequest(request => {
                const update = { ...request, role: structuredClone(request.role) };
                update.role[0].site = clientConfig?.accountRequest?.site;
                return update;
            })
        }
        if (clientConfig?.accountRequest?.level) {
            setRequest(request => {
                const update = { ...request, role: structuredClone(request.role) };
                update.role[0].level = clientConfig?.accountRequest?.level;
                return update;
            })
        }
    }, [clientConfig?.accountRequest?.site, clientConfig?.accountRequest?.level]);

    const handleUpdate = useCallback((delta) => {
        setRequest(request => ({ ...request, ...delta }));
    }, [])

    const submit = useCallback(async () => {
        if (formRef.current?.checkValidity()) {
            setSubmitted(true);
            const toSend = { ...request };
            if (sitrep) {
                toSend.sitrep_access = toSend.role
            }
            if (log) {
                toSend.log_access = toSend.role
            }
            try {
                await createAccountRequest(network, toSend);
                setSuccess(true);
            } catch {
                setSuccess(false);
            } finally {
                setSubmitted(false);
            }
        }
    }, [network, formRef, sitrep, log, request]);

    return <div style={{ maxWidth: '1200px', margin: 'auto' }}>
        { success === undefined && <>
            <ReactForm ref={formRef}>
                <Card>
                    {t('requestAccountInstructions')}
                </Card>
                <AccountRequestDetails value={request} disabled={false} onChange={handleUpdate}></AccountRequestDetails>
                <Card title={t('access')}>
                    <Row>
                        { clientConfig?.enableSites && !clientConfig?.accountRequest?.site && <Col md>
                            <SiteDropdown required site={request.role[0].site} level={request.role[0].level} type={request.role[0].type} perm="any" onChange={value => handleUpdate({ role: [{ ...request.role[0], ...value }]})}></SiteDropdown>
                        </Col> }
                        { clientConfig?.enableLevels && !clientConfig?.accountRequest?.level && <Col md>
                            <LevelDropdown required site={request.role[0].site} level={request.role[0].level} type={request.role[0].type} perm="any" onChange={value => handleUpdate({ role: [{ ...request.role[0], ...value }]})}></LevelDropdown>
                        </Col>}
                        <Col md>
                            <TypeDropdown required site={request.role[0].site} level={request.role[0].level} type={request.role[0].type} perm="any" onChange={value => handleUpdate({ role: [{ ...request.role[0], ...value }]})}></TypeDropdown>
                        </Col>
                        <Col md>
                            <AccessDropdown value={request.role[0].access} required access={[0,1,2]} onChange={access => handleUpdate({ role: [{ ...request.role[0], access }]})}></AccessDropdown>
                        </Col>
                    </Row>
                    <Row>
                        { clientConfig?.accountRequest.enableSitrep && <Col xs="auto">
                            <Checkbox checked={sitrep} placeholder={t('sitrep')} onChange={setSitrep}></Checkbox>
                        </Col> }
                        { clientConfig?.accountRequest.enableLog && <Col xs="auto">
                            <Checkbox checked={log} placeholder={t('log_one')} onChange={setLog}></Checkbox>
                        </Col> }
                        <Col xs></Col>
                    </Row>
                </Card>
            </ReactForm>
            <DefaultButton className="w-100" kind="submit" variant="solid-success" processing={submitted} onClick={submit}></DefaultButton>
        </> }
        { success === true && <Card>{t('requestAccountSuccess')}</Card> }
        { success === false && <Card>{t('requestAccountFailure')}</Card> }
    </div>
}
export const AngularRequestAccount = () => <RequestAccountImpl></RequestAccountImpl>
export const RequestAccount = () => {
    const { t } = useTranslation();
    const { setTitle } = usePublicNavBar();

    useEffect(() => setTitle(t('accountRequest_one')), [setTitle, t])

    return <RequestAccountImpl></RequestAccountImpl>
}